export function parseAmericanOdds(value) {
  const normalized = String(value ?? "").trim();
  if (!/^[+-]?\d+$/.test(normalized)) return null;
  const odds = Number(normalized);
  if (!Number.isSafeInteger(odds) || Math.abs(odds) < 100) return null;
  const probability = odds > 0 ? 100 / (odds + 100) : -odds / (-odds + 100);
  return { odds, probability };
}

export const ASSUMED_ONE_SIDED_HOLD = 0.045;

export function devigAmericanOdds(americanOdds, oppositeOdds = "") {
  const priced = parseAmericanOdds(americanOdds);
  if (!priced) return null;
  const opposite = String(oppositeOdds ?? "").trim() === ""
    ? null
    : parseAmericanOdds(oppositeOdds);
  if (String(oppositeOdds ?? "").trim() !== "" && !opposite) return null;
  if (opposite) {
    return priced.probability / (priced.probability + opposite.probability);
  }

  const pricedDecimal = priced.odds > 0
    ? 1 + priced.odds / 100
    : 1 + 100 / -priced.odds;
  const fairDecimal = 1 + (pricedDecimal - 1) * (1 + ASSUMED_ONE_SIDED_HOLD);
  return 1 / fairDecimal;
}

export function formatAmericanOdds(odds) {
  return odds > 0 ? `+${odds}` : String(odds);
}

export function clientFallbackProbability(legs) {
  if (legs.length === 0) return null;
  return legs.reduce(
    (value, leg) => value * (leg.marginalProbability ?? leg.demoProbability),
    1,
  );
}

export function fairAmericanOdds(probability) {
  if (!Number.isFinite(probability) || probability <= 0 || probability >= 1) return null;
  const odds = probability >= 0.5
    ? -(probability / (1 - probability)) * 100
    : ((1 - probability) / probability) * 100;
  return Math.round(odds);
}

export function clientFallbackAnalysis(groups) {
  const populatedGroups = groups.filter((group) => group.legs.length > 0);
  if (populatedGroups.length === 0) return null;

  const perGame = populatedGroups.map((group) => {
    const probability = clientFallbackProbability(group.legs);
    return {
      gameId: group.game.id,
      sport: group.game.sport,
      fairProbability: probability,
      independentProbability: probability,
      correlationDelta: 0,
      fairOdds: fairAmericanOdds(probability),
      void: false,
      voidReason: null,
    };
  });
  const independentProbability = perGame.reduce(
    (value, game) => value * game.independentProbability,
    1,
  );

  return {
    fairProbability: independentProbability,
    independentProbability,
    fairOdds: fairAmericanOdds(independentProbability),
    correlationDelta: 0,
    perGame,
    void: false,
    voidReason: null,
  };
}

export function parlayAnalysis(response) {
  if (!response || typeof response !== "object") return null;
  const requiredNumbers = [
    response.fairProbability,
    response.independentProbability,
    response.correlationDelta,
  ];
  if (requiredNumbers.some((value) => !Number.isFinite(value))) {
    throw new TypeError("Invalid pricing response");
  }
  if (!Array.isArray(response.perGame) || typeof response.void !== "boolean") {
    throw new TypeError("Invalid pricing response");
  }
  return response;
}

export function pricingLeg(leg, game) {
  const side = demoSideKey(game, leg.team);
  return {
    id: leg.id,
    gameId: leg.gameId,
    sport: leg.sport,
    market: leg.market,
    team: leg.team,
    player: leg.player,
    direction: leg.direction,
    line: leg.line,
    probability: leg.demoProbability,
    ...(side ? { side } : {}),
  };
}

// One stable identity token for a team or a player. Case, accents, punctuation and whitespace all
// collapse, so "New York Yankees", "new york yankees" and " New-York  Yankees! " are the same tag —
// which is what lets the pricing model recognise same-side and same-player legs on a manually typed
// slip, where nothing else identifies them.
export function normalizeLegTag(label) {
  return String(label ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

// Which structured fields each own-slip market actually uses. The builder renders from this and the
// validator reads the same table, so a market can never be shown one set of inputs and judged by
// another. `required` lists the fields a leg cannot price without: a team market is meaningless
// without its team and its number, while a player prop is already identified by the player — naming
// his team only sharpens the correlation, so it stays optional (as does a yes/no prop's line).
export const OWN_MARKET_FIELDS = {
  moneyline: { team: true, player: false, line: false, direction: false, required: ["team"] },
  spread: { team: true, player: false, line: true, direction: false, required: ["team", "line"] },
  total: { team: false, player: false, line: true, direction: true, required: ["line"] },
  team_total: { team: true, player: false, line: true, direction: true, required: ["team", "line"] },
  player_prop: { team: true, player: true, line: true, direction: true, required: ["player"] },
};

export function ownMarketFields(marketType) {
  return OWN_MARKET_FIELDS[marketType] ?? null;
}

// A game group owns exactly two sides, named once. Legs pick a side by key, never by retyping a
// name, so the second leg on a team physically cannot introduce a third spelling of it.
export const SIDE_KEYS = ["a", "b"];
export const SIDE_LABELS = { a: "Team A", b: "Team B" };

export function gameSideName(game, key) {
  return String(game?.sides?.[key] ?? "").trim();
}

// Only a NAMED side can be picked: an unnamed slot is not an identity the pricing model can use.
export function gameSideOptions(game) {
  return SIDE_KEYS
    .map((key) => ({ key, name: gameSideName(game, key) }))
    .filter((side) => side.name !== "");
}

export function legSideKey(game, leg) {
  const key = String(leg?.side ?? "");
  return SIDE_KEYS.includes(key) && gameSideName(game, key) ? key : "";
}

function tagTokens(tag) {
  return new Set(String(tag ?? "").split("-").filter(Boolean));
}

function oneSideTokens(left, right) {
  const [a, b] = [tagTokens(left), tagTokens(right)];
  if (a.size === 0 || b.size === 0) return false;
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  return [...small].every((token) => large.has(token));
}

// Which side a team name refers to: an exact tag first, then a UNIQUE token-subset match, so
// "Red Sox" finds "Boston Red Sox" while "Sox" between two Sox teams matches neither. Same rule as
// the server's fold — the two halves of one guarantee.
export function sideKeyForName(game, ...names) {
  const wanted = names.map(normalizeLegTag).filter(Boolean);
  if (wanted.length === 0) return "";
  const tagged = SIDE_KEYS
    .map((key) => ({ key, tag: normalizeLegTag(gameSideName(game, key)) }))
    .filter((side) => side.tag !== "");
  const exact = tagged.find((side) => wanted.includes(side.tag));
  if (exact) return exact.key;
  const near = tagged.filter((side) => wanted.some((name) => oneSideTokens(side.tag, name)));
  return near.length === 1 ? near[0].key : "";
}

// Picking a real game REPLACES both sides, so a leg keeps its side only while that side still names
// the same team. Anything else is cleared, because silently re-pointing a leg at another team is
// exactly the failure this model exists to prevent.
export function remapLegSides(game, nextSides) {
  const next = { sides: nextSides };
  return (game?.legs ?? []).map((leg) => {
    const current = normalizeLegTag(gameSideName(game, leg.side));
    const key = current ? sideKeyForName(next, current) : "";
    if (key === String(leg.side ?? "")) return leg;
    return { ...leg, side: key, teamLock: key ? leg.teamLock : null };
  });
}

export function sidePatch(side) {
  return { side: SIDE_KEYS.includes(side) ? side : "", teamLock: null };
}

export function ownSlipTeamTag(game, leg) {
  return normalizeLegTag(gameSideName(game, legSideKey(game, leg)));
}

// The sample slate already knows which two teams a fixture has, so its legs declare a side too and
// never depend on the server reading one out of the team text.
export function demoSideKey(game, team) {
  const tag = normalizeLegTag(team);
  if (!tag) return "";
  if (normalizeLegTag(game?.away?.abbreviation) === tag) return "a";
  if (normalizeLegTag(game?.home?.abbreviation) === tag) return "b";
  return "";
}

// An ESPN athlete id is the stablest identity available; the normalized name is the fallback for the
// manual path. Both legs naming one player resolve through the same per-game roster map, so a slip
// never splits one player across two tags.
export function ownSlipPlayerTag(leg) {
  const espnId = String(leg?.playerId ?? "").trim();
  return espnId ? `espn-${normalizeLegTag(espnId)}` : normalizeLegTag(leg?.player);
}

function ownSlipLine(leg) {
  const raw = String(leg?.line ?? "").trim();
  if (raw === "") return null;
  const line = Number(raw);
  return Number.isFinite(line) ? line : null;
}

export function ownSlipMissingFields(game, leg) {
  const fields = leg ? ownMarketFields(leg.marketType) : null;
  if (!fields) return ["market"];
  const present = {
    team: Boolean(ownSlipTeamTag(game, leg)),
    player: Boolean(ownSlipPlayerTag(leg)),
    // The implication rules (a spread implies the moneyline, alternate lines nest) are only exact
    // when the line is there, so a team market without its number is incomplete rather than vague.
    line: String(leg.line ?? "").trim() !== "",
  };
  return fields.required.filter((field) => !present[field]);
}

export function isValidOwnSlipLeg(game, leg) {
  const fields = leg ? ownMarketFields(leg.marketType) : null;
  if (!fields || !parseAmericanOdds(leg.americanOdds)) return false;
  if (String(leg.oppositeOdds ?? "").trim() && !parseAmericanOdds(leg.oppositeOdds)) return false;
  if (fields.direction && !["over", "under"].includes(leg.direction)) return false;
  if (ownSlipMissingFields(game, leg).length > 0) return false;

  const raw = String(leg.line ?? "").trim();
  return !raw || Number.isFinite(Number(raw));
}

export function ownSlipPricingLeg(game, leg) {
  if (!isValidOwnSlipLeg(game, leg)) return null;
  const fields = ownMarketFields(leg.marketType);
  const americanOdds = parseAmericanOdds(leg.americanOdds).odds;
  const opposite = String(leg.oppositeOdds ?? "").trim()
    ? parseAmericanOdds(leg.oppositeOdds).odds
    : null;
  const side = fields.team ? legSideKey(game, leg) : "";
  const team = side ? ownSlipTeamTag(game, leg) : "";
  const player = fields.player ? ownSlipPlayerTag(leg) : "";

  return {
    id: leg.id,
    gameId: game.id,
    sport: game.sport,
    market: leg.marketType,
    team: team || null,
    player: player || null,
    direction: fields.direction ? leg.direction : "over",
    line: fields.line ? ownSlipLine(leg) : null,
    americanOdds,
    ...(opposite === null ? {} : { oppositeOdds: opposite }),
    // The side the leg picked, so the model is TOLD which legs face each other instead of guessing
    // it from two spellings of a team name.
    ...(team ? { side } : {}),
  };
}

// Display only — the API is sent tags, never this text.
export function ownSlipLegLabel(game, leg) {
  const fields = leg ? ownMarketFields(leg.marketType) : null;
  if (!fields) return "";
  const team = gameSideName(game, legSideKey(game, leg));
  const player = String(leg.player ?? "").trim();
  const raw = String(leg.line ?? "").trim();
  const line = raw === "" ? "" : `${Number(raw) > 0 && leg.marketType === "spread" ? "+" : ""}${raw}`;
  const side = leg.direction === "under" ? "U" : "O";
  if (leg.marketType === "moneyline") return team ? `${team} moneyline` : "";
  if (leg.marketType === "spread") return team && line ? `${team} ${line}` : "";
  if (leg.marketType === "total") return line ? `Game total ${side}${raw}` : "";
  if (leg.marketType === "team_total") return team && line ? `${team} team total ${side}${raw}` : "";
  if (!player) return "";
  return raw ? `${player} ${side}${raw}` : player;
}

export function estimatedPriceCost(fairProbability, offeredOdds) {
  const offered = parseAmericanOdds(offeredOdds);
  if (!Number.isFinite(fairProbability) || fairProbability <= 0 || !offered) return null;
  const decimalPayout = offered.odds > 0 ? 1 + offered.odds / 100 : 1 + 100 / -offered.odds;
  return 1 - fairProbability * decimalPayout;
}

export function hitsIn100(probability) {
  return probability === null ? null : Math.round(probability * 100);
}

export function priceVerdict(demoProbability, operatorProbability) {
  if (demoProbability === null || operatorProbability === null) {
    return { label: "Add legs and a valid price", tone: "neutral" };
  }

  const gap = demoProbability - operatorProbability;
  if (gap > 0.03) return { label: "Looks favorable", tone: "positive" };
  if (gap < -0.03) return { label: "Looks expensive", tone: "negative" };
  return { label: "Looks close to fair", tone: "neutral" };
}
